/**
 * Comuniverse's progress push — the body of
 * `PUT /api/partners/comuniverse/learners/{citizen_id}/curriculum`, the
 * learner's WHOLE state at the school (snapshot), as its progress contract
 * states it, plus the `curriculum` extension Tonnie asked for (optional: the
 * learner's full path, from the start level to the target, so completion can
 * be counted — without it the World works in reduced mode, D8).
 *
 *   updated_at                     the school's timestamp of the state, ISO 8601 UTC (`Z`)
 *   start_level · current_level ·  the school's 0–700 scale
 *   target_level
 *   abilities[]                    the current level's abilities — id (integer),
 *                                  title (≤ 300), mastered (0–1); at most 500
 *   curriculum[] (optional)        the levels of the path — level (0–700),
 *                                  title (≤ 60), abilities[] with their `order`
 *                                  (≥ 1); at most 20 levels and 500 abilities
 *
 * An ability's id is the mission's key, so it appears once in `abilities` and
 * once in the whole `curriculum`; a level appears once in the path.
 *
 * Forward-compatible: a key the contract does not name is ignored (stripped
 * here — the API records the top-level ones it saw, and keeps the body as it
 * arrived). The body's size (≤ 256 KB) is the route's to enforce.
 */
import { z } from 'zod';

/** The school's level scale. */
export const COMUNIVERSE_LEVEL_MIN = 0;
export const COMUNIVERSE_LEVEL_MAX = 700;
/** The most abilities a push names — in `abilities`, and in the whole `curriculum`. */
export const COMUNIVERSE_MAX_ABILITIES = 500;
/** The most levels a path holds. */
export const COMUNIVERSE_MAX_CURRICULUM_LEVELS = 20;
export const COMUNIVERSE_ABILITY_TITLE_MAX = 300;
export const COMUNIVERSE_LEVEL_TITLE_MAX = 60;
/** The largest body the route accepts. */
export const COMUNIVERSE_PROGRESS_MAX_BODY_BYTES = 256 * 1024;

const level = z.number().min(COMUNIVERSE_LEVEL_MIN).max(COMUNIVERSE_LEVEL_MAX);
const mastered = z.number().min(0).max(1);
const abilityId = z.int();
const abilityTitle = z.string().max(COMUNIVERSE_ABILITY_TITLE_MAX);

/** The ids that appear more than once. */
function repeated(ids: readonly number[]): number[] {
  const seen = new Set<number>();
  const twice = new Set<number>();
  for (const id of ids) (seen.has(id) ? twice : seen).add(id);
  return [...twice];
}

const curriculumAbilitySchema = z.object({
  id: abilityId,
  title: abilityTitle,
  order: z.int().min(1),
  mastered,
});

const curriculumLevelSchema = z.object({
  level,
  title: z.string().max(COMUNIVERSE_LEVEL_TITLE_MAX),
  abilities: z.array(curriculumAbilitySchema).max(COMUNIVERSE_MAX_ABILITIES),
});

export const comuniverseCurriculumPayloadSchema = z
  .object({
    updated_at: z.iso.datetime(),
    start_level: level,
    current_level: level,
    target_level: level,
    abilities: z
      .array(z.object({ id: abilityId, title: abilityTitle, mastered }))
      .max(COMUNIVERSE_MAX_ABILITIES),
    curriculum: z.array(curriculumLevelSchema).max(COMUNIVERSE_MAX_CURRICULUM_LEVELS).optional(),
  })
  .superRefine((push, ctx) => {
    if (repeated(push.abilities.map((a) => a.id)).length > 0) {
      ctx.addIssue({ code: 'custom', path: ['abilities'] });
    }
    if (!push.curriculum) return;
    const abilities = push.curriculum.flatMap((l) => l.abilities);
    if (abilities.length > COMUNIVERSE_MAX_ABILITIES) {
      ctx.addIssue({ code: 'custom', path: ['curriculum'] });
    }
    if (repeated(abilities.map((a) => a.id)).length > 0) {
      ctx.addIssue({ code: 'custom', path: ['curriculum'] });
    }
    if (repeated(push.curriculum.map((l) => l.level)).length > 0) {
      ctx.addIssue({ code: 'custom', path: ['curriculum'] });
    }
  });
export type ComuniverseCurriculumPayloadInput = z.infer<typeof comuniverseCurriculumPayloadSchema>;

/** The top-level keys the contract names — any other is ignored, and recorded. */
export const COMUNIVERSE_CURRICULUM_PAYLOAD_KEYS: readonly string[] = Object.keys(
  comuniverseCurriculumPayloadSchema.shape
);
